import { useWriteContract } from "@abraxas/ui/web3"
import { config } from "@repo/loomlock/client"
import { useCallback, useMemo } from "react"
import { useAccount } from "wagmi"
import { ABIDirectListingsLogic } from "../abis/DirectListingsLogic"
import { MARKETPLACE_CHAIN_ID } from "../marketplace-chain-id"
import { Listing, NATIVE_TOKEN_ADDRESS } from "../types"
import { useBalances } from "@/shared/indexer/useBalances"
import { PRODUCT_TYPES } from "@repo/loomlock/model-types"

interface UseBuyListingParams {
  listing: Listing
}

export function useBuyListing({ listing }: UseBuyListingParams) {
  const { writeContractAsync, isPending } = useWriteContract("buy-listing")
  const { address } = useAccount()
  const { refetch: refetchBalances } = useBalances({
    address: address || null,
    productType: PRODUCT_TYPES,
  })
  const buyListing = useCallback(
    async (quantity: bigint) => {
      const marketplaceAddress =
        config[MARKETPLACE_CHAIN_ID]?.contracts.marketplace

      if (!marketplaceAddress) {
        throw new Error("Marketplace not deployed on this network")
      }

      const value =
        listing.currency === NATIVE_TOKEN_ADDRESS
          ? BigInt(listing.pricePerToken) * BigInt(quantity)
          : 0n

      const receipt = await writeContractAsync({
        address: marketplaceAddress,
        abi: ABIDirectListingsLogic,
        functionName: "buyFromListing",
        args: [listing.listingId, address, quantity, listing.currency, value],
        chainId: MARKETPLACE_CHAIN_ID,
        loading: "Buying from listing...",
        success: "Purchase successful!",
        error: "Failed to purchase",
        value: value as any,
      })

      refetchBalances()
      return receipt
    },
    [writeContractAsync, listing, address, refetchBalances],
  )

  const canBuy = useMemo(() => {
    if (!address) {
      return { canBuy: false, reason: "Wallet not connected" }
    }

    const isOwner =
      address.toLowerCase() === listing.listingCreator.toLowerCase()
    if (isOwner) {
      return { canBuy: false, reason: "Cannot buy your own listing" }
    }

    const isExpired = Number(listing.endTimestamp) * 1000 < Date.now()
    if (isExpired) {
      return { canBuy: false, reason: "Listing has expired" }
    }

    return { canBuy: true, reason: null }
  }, [address, listing])

  return {
    buyListing,
    isPending,
    canBuy: canBuy.canBuy,
    cannotBuyReason: canBuy.reason,
  }
}
